<template>
<!-- eslint-disable max-len -->
<span>
  <top-bar v-if="narrative.showNavbar">
    <template :slot="narrative.showMetrics ? 'challenge' : 'default'">
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks
        This is what the following line used to be:
        {{ narrativeNavbarText }}
      -->
      <p v-html="narrativeNavbarText"></p>
    </template>
    <cash-navbar v-if="narrative.showMetrics" slot="cash"></cash-navbar>
    <metrics-navbar v-if="narrative.showMetrics" slot="metrics"></metrics-navbar>
  </top-bar>
  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showVideoPlayer" ref="videoPlayer" class="video-player" :style="{ left: rightVideo ? '50px' : 'unset', right: leftVideo ? '50px' : 'unset' }" @click.prevent.stop="pauseVideo" v-focus tabindex="1">
      <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
        <video
          v-if="showVideo"
          ref="video"
          :src="resolveAssetPath(narrative.video, 'videos')"
          class="video"
          :autoplay="!disableAutoplay"
          playsinline
          @ended="onEnded"
          @play="onPlay"
          @pause="onPause"
          @loadeddata="onLoad"
        ></video>
      </transition>
      <button
        v-if="showPlayButton"
        class="video-button play-button"
        @click.prevent.stop="playVideo"
        v-focus
        tabindex="2"
      ></button>
      <button
        v-if="showReplayButton"
        class="video-button replay-button"
        @click.prevent.stop="replayVideo"
        v-focus
        tabindex="2"
      ></button>
      <tooltip
        target="videoOrImage"
      >
      </tooltip>
    </button>
  </transition>

  <template v-if="false">
    <transition :enter-active-class="leftVideo && leftCharacterAnimateEnter ? 'animated slideInLeft' : rightVideo && rightCharacterAnimateEnter ? 'animated slideInRight' : ''" :leave-active-class="leftVideo && leftCharacterAnimateLeave ? 'animated slideOutLeft' : rightVideo && rightCharacterAnimateLeave ? 'animated slideOutRight' : ''">
      <narrator v-if="showNarrator" :character="characters[leftVideo ? narrative.leftCharacter : narrative.rightCharacter]" :left="narrative.type === 'leftVideo'" :tooltip-target="leftVideo ? 'characterLeft' : 'characterRight'"></narrator>
    </transition>

    <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
      <narrator-notification v-if="showNarratorNotification" class="narrator-notification" :style="{ left: narrative.type === 'leftVideo' ? '10px' : 'unset', right: rightVideo ? '10px' : 'unset' }" :left="narrative.type === 'leftVideo'">
        <!--
          Never use v-html with end-user generated content, because it's a vector for XSS attacks,
          but this is okay here since narrativeText is only used/set internally.
          AFAIK, this is the simplest way to support hyperlinks in dialogue, and it also enables any html formatting.
          This is what the following line used to be:
          {{ narrativeText }}
        -->
        <p v-html="narrativeText"></p>
      </narrator-notification>
    </transition>
  </template>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar
      :next="showNarratorNotification ? onComplete : undefined"
      :back="(enableBack ? (() => onComplete(true)) : undefined)"
      :tabindex="3"
      ref="bottomBar"
      :noBar="true"
      narrative-style
    >
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
      <span slot="back">
        <span v-html="(narrative && narrative.backButtonText) ? narrative.backButtonText : ((game && game.backButtonDefaultText) ? game.backButtonDefaultText : 'Back')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';

import TopBar from '../ui/TopBar';
import MetricsNavbar from '../ui/MetricsNavbar';
import CashNavbar from '../ui/CashNavbar';
import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';
import Tooltip from '../ui/Tooltip';

export default {
  // $emit 'input' on complete
  components: {
    TopBar,
    MetricsNavbar,
    CashNavbar,
    Narrator,
    NarratorNotification,
    BottomBar,
    Tooltip,
  },
  data() {
    return {
      showNarrator: false,
      showNarratorNotification: false,
      showVideoPlayer: false,
      showVideo: false,
      showPlayButton: false,
      showReplayButton: false,
      videoStartVolume: 0.25
    };
  },
  computed: {
    ...mapGetters([
      'disableAutoplay',
      'characters',
      'tooltip',
      'narrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'leftCharacterAnimateEnter',
      'leftCharacterAnimateLeave',
      'rightCharacterAnimateEnter',
      'rightCharacterAnimateLeave',
      'game'
    ]),
    enableBack() {
      return this.showNarratorNotification
        && (this.narrative.forceEnableBack || (this.game.enableBack && !(this.narrative.preventEnableBack)));
    },
    leftVideo() {
      return this.narrative.type === 'leftVideo';
    },
    rightVideo() {
      return this.narrative.type === 'rightVideo';
    },
    fullVideo() {
      return this.narrative.type === 'fullVideo';
    }
  },
  methods: {
    ...mapActions([
      'stopSounds'
    ]),
    async show() {
      this.showNarrator = true;
      this.showVideoPlayer = true;
      await Vue.nextTick();
      await new Promise(resolve => setTimeout(resolve, 600));
      this.showVideo = true;
      await Vue.nextTick();

      const { video } = this.$refs;
      video.volume = this.videoStartVolume;

      await new Promise(resolve => setTimeout(resolve, 4000));
      this.showNarratorNotification = true;
    },
    async hide() {
      const { video } = this.$refs;
      if (video) video.pause();

      this.showNarratorNotification = false;
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.showNarrator =
        (this.leftVideo && !this.leftCharacterAnimateLeave) ||
        (this.rightVideo && !this.rightCharacterAnimateLeave);
      this.showVideoPlayer = false;
      this.showVideo = false;
    },
    async reset() {
      await this.hide();
      await Vue.nextTick();
      await this.show();
    },
    pauseVideo() {
      const { video } = this.$refs;
      if (!video) return;
      this.showPlayButton = true;
      video.pause();
    },
    onLoad() {
      this.showPlayButton = true;
    },
    onPause() {
      this.showPlayButton = true;
      this.handleAudioPlayAgain();
    },
    onPlay() {
      this.showPlayButton = false;
      this.handleAudioPause();
    },
    onEnded() {
      if (this.narrative && this.narrative.isReplayable) {
        this.showPlayButton = false;
        this.showReplayButton = true;
      } else {
        this.$refs.bottomBar.navigate();
      }
    },
    playVideo() {
      const { video, videoPlayer } = this.$refs;
      if (!video) return;
      video.play();
      this.showPlayButton = false;
      videoPlayer.focus();
    },
    replayVideo() {
      const { video, videoPlayer } = this.$refs;
      if (!video) return;
      video.currentTime = 0;
      video.play();
      this.showReplayButton = false;
      videoPlayer.focus();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete(back = false) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      await this.hide();
      this.$emit('input', back);
    },
    handleAudioPlayAgain() {
      this.setAudioPlaying(true);
      this.setAudioPause(false);
    },
    handleAudioPause() {
      this.setAudioPause(true);
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    }
  },
  created() {
    this.showNarrator =
      (this.leftVideo && !this.leftCharacterAnimateEnter) ||
      (this.rightVideo && !this.rightCharacterAnimateEnter);
  },
  async mounted() {
    this.stopSounds(); // ensures any previous voice line is cut off before the video plays
    await this.show();
  }
};
</script>

<style lang="scss" scoped>
.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}

.video-player {
  background-color: black;
  /*background-image: url('/static/ui/customer/MovieFrame.jpg');
  background-repeat: no-repeat;
  background-size: cover; */
  border: none;
  position: absolute;
  height: 100%;
  width: 100%;
  overflow: hidden;
}
.video-player:focus {
  outline: none;
}
.video {
  height: 100%;
  width: 100%;
  object-fit: contain;
}
.video-button {
  -webkit-filter: drop-shadow(0px 0px 16px); /* Safari 6.0 - 9.0 */
  filter: drop-shadow(0px 0px 16px);
  background-color: transparent;
  background-image: url('/static/ui/MovieIcon_Play.png');
  background-repeat: none;
  background-size: cover;
  background-position: center center;
  border: none;
  width: 150px;
  height: 150px;
  position: absolute;
  top: 350px;
  left: 750px;
}
.video-button:focus {
  outline: none;
}
.play-button {
  background-image: url('/static/ui/MovieIcon_Play.png');
}
.replay-button {
  background-image: url('/static/ui/MovieIcon_Replay.png');
}
.narrator-notification {
  bottom: 205px;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeVideoFull.vue